<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>

    <admin-breadcrumb
        :items="[
        {
          text: 'Reports',
          path: { name: 'admin-reports' }
        }
      ]"
        name="Incomplete Applications"
        :title="pageTitle"
    />

    <admin-panel with-header>
      <b-table
          :fields="reportColumns"
          :items="reportRows"
          table-class="text-nowrap"
          responsive
          hover
          show-empty
          empty-text="No incomplete applications found"
      >
        <template #head(application_started_at)="data">
          <span
              class="sortable-column"
              @click="toggleSort('application_started_at')"
          >
            {{ data.label }}
            {{ renderSortIcon('application_started_at') }}
          </span>
        </template>

        <template #head(last_activity_at)="data">
          <span
              class="sortable-column"
              @click="toggleSort('last_activity_at')"
          >
            {{ data.label }}
            {{ renderSortIcon('last_activity_at') }}
          </span>
        </template>

        <template #cell(applicant)="data">
          <div class="font-weight-bold">
            {{ data.item.applicant_name || '—' }}
          </div>

          <div class="small text-muted">
            {{ data.item.email || '—' }}
          </div>
        </template>

        <template #cell(phone)="data">
          {{ data.item.phone || '—' }}
        </template>

        <template #cell(company)="data">
          <div>
            {{ data.item.company_name || '—' }}
          </div>

          <div
              v-if="data.item.license_number"
              class="small text-muted"
          >
            License: {{ data.item.license_number }}
          </div>
        </template>

        <template #cell(state)="data">
          {{ data.item.state || '—' }}
        </template>

        <template #cell(stage)="data">
          <b-badge
              :variant="getStageVariant(data.item.stage)"
          >
            {{ getStageLabel(data.item.stage) }}
          </b-badge>
        </template>

        <template #cell(api_key_status)="data">
          <b-badge
              :variant="getStatusVariant(data.item.api_key_status)"
          >
            {{ getApiKeyStatusLabel(data.item.api_key_status) }}
          </b-badge>
        </template>

        <template #cell(account_status)="data">
          <b-badge
              :variant="getStatusVariant(data.item.account_status)"
          >
            {{ getAccountStatusLabel(data.item.account_status) }}
          </b-badge>
        </template>

        <template #cell(application_started_at)="data">
          <div>
            {{ formatDate(data.item.application_started_at) }}
          </div>

          <div class="small text-muted">
            {{ formatTime(data.item.application_started_at) }}
          </div>
        </template>

        <template #cell(last_activity_at)="data">
          <div>
            {{ formatDate(data.item.last_activity_at) }}
          </div>

          <div class="small text-muted">
            {{ formatTime(data.item.last_activity_at) }}
          </div>
        </template>

        <template #cell(incomplete_for)="data">
          <span
              v-if="data.item.application_started_at"
              :class="getIncompleteAgeClass(data.item.application_started_at)"
          >
            {{ getIncompleteAge(data.item.application_started_at) }}
          </span>

          <span v-else>—</span>
        </template>
      </b-table>

      <template v-slot:header>
        <div class="d-flex justify-content-between">
          <div class="d-flex">
            <div class="mr-3">
              <strong class="text-uppercase">
                Start Date:
              </strong>

              {{ startDate }}

              <b-form-datepicker
                  v-model="filters.start_date"
                  class="ml-2"
                  :max="maxDate"
                  button-only
                  button-variant="primary"
                  @input="onStartDateChanged"
              />
            </div>

            <div>
              <strong class="text-uppercase">
                End Date:
              </strong>

              {{ endDate }}

              <b-form-datepicker
                  v-model="filters.end_date"
                  class="ml-2"
                  :max="maxDate"
                  button-only
                  button-variant="primary"
                  @input="onEndDateChanged"
              />
            </div>
          </div>

          <div>
            <b-btn
                variant="primary"
                class="mr-2"
                v-b-tooltip.hover
                title="Download CSV"
                :disabled="!reportRows.length"
                @click.prevent="downloadCsv"
            >
              <b-icon-download/>
            </b-btn>

            <b-btn
                variant="primary"
                v-b-tooltip.hover
                title="Reset Filters"
                @click.prevent="resetFilters"
            >
              <b-icon-arrow-counterclockwise/>
            </b-btn>
          </div>
        </div>
      </template>

      <template v-slot:header-right>
        <div class="mt-n2 d-flex flex-wrap align-items-center">
          <b-input-group
              class="mr-2 my-2 incomplete-applications-search"
          >
            <b-form-input
                v-model.trim="filters.search"
                placeholder="Name, email, company or license"
                @keyup.enter="loadReportData"
            />

            <b-input-group-append>
              <b-btn
                  variant="primary"
                  @click="loadReportData"
              >
                Search
              </b-btn>
            </b-input-group-append>
          </b-input-group>

          <b-form-select
              class="w-auto mr-2 my-2"
              v-model="filters.stage"
              :options="stageOptions"
              @change="loadReportData"
          />
        </div>
      </template>
    </admin-panel>
  </b-container>
</template>

<script>
import AdminPreloader from "../components/AdminPreloader";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import AdminPanel from "../components/AdminPanel";
import moment from "moment";
import {API_ROUTES} from "../api";

export default {
  name: "AdminReportIncompleteApplications",

  components: {
    AdminPanel,
    AdminBreadcrumb,
    AdminPreloader,
  },

  data() {
    const query = this.$route.query;

    return {
      loading: true,

      report_data: [],

      sortableColumns: [
        "application_started_at",
        "last_activity_at",
      ],

      filters: {
        start_date:
            query.start_date ??
            moment().startOf("month").format("YYYY-MM-DD"),

        end_date:
            query.end_date ??
            moment().format("YYYY-MM-DD"),

        search:
            query.search ??
            "",

        stage:
            query.stage ??
            null,

        sort:
            query.sort ??
            "application_started_at",

        sort_direction:
            query.sort_direction ??
            "desc",
      },
    };
  },

  methods: {
    setQueryVar(key, value) {
      const query = {
        ...this.$route.query,
      };

      if (
          value === null ||
          value === "" ||
          typeof value === "undefined"
      ) {
        delete query[key];
      } else {
        query[key] = value;
      }

      this.$router.push({
        query,
      });
    },

    onStartDateChanged(startDate) {
      if (
          moment(startDate).isAfter(
              moment(this.filters.end_date)
          )
      ) {
        this.filters.end_date = startDate;
      }

      this.loadReportData();
    },

    onEndDateChanged(endDate) {
      if (
          moment(endDate).isBefore(
              moment(this.filters.start_date)
          )
      ) {
        this.filters.start_date = endDate;
      }

      this.loadReportData();
    },

    toggleSort(field) {
      if (!this.sortableColumns.includes(field)) {
        return;
      }

      if (this.filters.sort === field) {
        this.filters.sort_direction =
            this.filters.sort_direction === "asc"
                ? "desc"
                : "asc";
      } else {
        this.filters.sort = field;
        this.filters.sort_direction = "desc";
      }

      this.loadReportData();
    },

    renderSortIcon(field) {
      if (this.filters.sort !== field) {
        return "";
      }

      return this.filters.sort_direction === "asc"
          ? "↑"
          : "↓";
    },

    loadReportData() {
      const params = {
        start_date: this.filters.start_date,
        end_date: this.filters.end_date,
        search: this.filters.search || null,
        stage: this.filters.stage,
        sort: this.filters.sort,
        sort_direction: this.filters.sort_direction,
      };

      this.loading = true;

      this.$api
          .get(
              API_ROUTES.ADMIN_REPORT_INCOMPLETE_APPLICATIONS,
              {
                params,
              }
          )
          .then(({data}) => {
            this.report_data = Array.isArray(
                data.report_data
            )
                ? data.report_data
                : [];
          })
          .catch(({response}) => {
            let message =
                "Failed to load incomplete applications report";

            if (
                response &&
                response.status === 422
            ) {
              message =
                  "Some of your params were incorrect";
            }

            this.$bvToast.toast(
                message,
                {
                  title: "Error!",
                  variant: "danger",
                }
            );
          })
          .finally(() => {
            this.loading = false;
          });
    },

    resetFilters() {
      this.filters = {
        start_date:
            moment()
                .startOf("month")
                .format("YYYY-MM-DD"),

        end_date:
            moment()
                .format("YYYY-MM-DD"),

        search: "",

        stage: null,

        sort:
            "application_started_at",

        sort_direction:
            "desc",
      };

      this.loadReportData();
    },

    getStageLabel(stage) {
      switch (stage) {
        case "api_key_required":
          return "API Key Required";

        case "account_creation_required":
          return "Account Creation Required";

        default:
          return "Incomplete";
      }
    },

    getStageVariant(stage) {
      switch (stage) {
        case "api_key_required":
          return "warning";

        case "account_creation_required":
          return "info";

        default:
          return "secondary";
      }
    },

    getApiKeyStatusLabel(status) {
      switch (status) {
        case "provided":
        case "completed":
          return "Provided";

        case "required":
        case "missing":
          return "Required";

        default:
          return "Unknown";
      }
    },

    getAccountStatusLabel(status) {
      switch (status) {
        case "created":
        case "completed":
          return "Created";

        case "required":
        case "missing":
          return "Required";

        default:
          return "Unknown";
      }
    },

    getStatusVariant(status) {
      switch (status) {
        case "provided":
        case "created":
        case "completed":
          return "success";

        case "required":
        case "missing":
          return "warning";

        default:
          return "secondary";
      }
    },

    formatDate(value) {
      if (!value) {
        return "—";
      }

      return moment(value).format("L");
    },

    formatTime(value) {
      if (!value) {
        return "";
      }

      return moment(value).format("LT");
    },

    getIncompleteAge(startedAt) {
      if (!startedAt) {
        return "—";
      }

      const started = moment(startedAt);
      const now = moment();

      const days = now.diff(
          started,
          "days"
      );

      if (days > 0) {
        return `${days} ${
            days === 1
                ? "day"
                : "days"
        }`;
      }

      const hours = now.diff(
          started,
          "hours"
      );

      if (hours > 0) {
        return `${hours} ${
            hours === 1
                ? "hour"
                : "hours"
        }`;
      }

      return "< 1 hour";
    },

    getIncompleteAgeClass(startedAt) {
      if (!startedAt) {
        return "";
      }

      const days = moment().diff(
          moment(startedAt),
          "days"
      );

      if (days >= 7) {
        return "text-danger font-weight-bold";
      }

      if (days >= 3) {
        return "text-warning font-weight-bold";
      }

      return "";
    },

    downloadCsv() {
      if (!this.reportRows.length) {
        return;
      }

      const escapeCsvValue = (value) => {
        const normalized = String(
            value ?? ""
        )
            .replace(/\r\n/g, " ")
            .replace(/\n/g, " ")
            .replace(/\r/g, " ")
            .replace(/"/g, '""')
            .trim();

        return `"${normalized}"`;
      };

      const headers = [
        "Applicant Name",
        "Email",
        "Phone",
        "Company",
        "License Number",
        "State",
        "Current Stage",
        "API Key Status",
        "Account Status",
        "Application Start",
        "Last Activity",
        "Incomplete For",
      ];

      const rows = this.reportRows.map(
          (row) => [
            escapeCsvValue(
                row.applicant_name
            ),

            escapeCsvValue(
                row.email
            ),

            escapeCsvValue(
                row.phone
            ),

            escapeCsvValue(
                row.company_name
            ),

            escapeCsvValue(
                row.license_number
            ),

            escapeCsvValue(
                row.state
            ),

            escapeCsvValue(
                this.getStageLabel(
                    row.stage
                )
            ),

            escapeCsvValue(
                this.getApiKeyStatusLabel(
                    row.api_key_status
                )
            ),

            escapeCsvValue(
                this.getAccountStatusLabel(
                    row.account_status
                )
            ),

            escapeCsvValue(
                row.application_started_at
                    ? moment(
                        row.application_started_at
                    ).format(
                        "YYYY-MM-DD HH:mm:ss"
                    )
                    : ""
            ),

            escapeCsvValue(
                row.last_activity_at
                    ? moment(
                        row.last_activity_at
                    ).format(
                        "YYYY-MM-DD HH:mm:ss"
                    )
                    : ""
            ),

            escapeCsvValue(
                this.getIncompleteAge(
                    row.application_started_at
                )
            ),
          ]
      );

      const csvContent = [
        headers
            .map(escapeCsvValue)
            .join(","),

        ...rows.map(
            (row) => row.join(",")
        ),
      ].join("\r\n");

      const blob = new Blob(
          [csvContent],
          {
            type: "text/csv;charset=utf-8;",
          }
      );

      const url =
          URL.createObjectURL(blob);

      const link =
          document.createElement("a");

      link.setAttribute(
          "href",
          url
      );

      link.setAttribute(
          "download",
          "incomplete_applications.csv"
      );

      document.body.appendChild(
          link
      );

      link.click();

      document.body.removeChild(
          link
      );

      URL.revokeObjectURL(url);
    },
  },

  computed: {
    pageTitle() {
      return "Incomplete Applications";
    },

    maxDate() {
      return moment().format(
          "YYYY-MM-DD"
      );
    },

    startDate() {
      return moment(
          this.filters.start_date
      ).format("L");
    },

    endDate() {
      return moment(
          this.filters.end_date
      ).format("L");
    },

    stageOptions() {
      return [
        {
          value: null,
          text: "All Incomplete Stages",
        },
        {
          value: "api_key_required",
          text: "API Key Required",
        },
        {
          value: "account_creation_required",
          text: "Account Creation Required",
        },
      ];
    },

    reportColumns() {
      return [
        {
          key: "applicant",
          label: "Applicant",
          thClass: "border-top-0",
          tdClass: "align-middle",
        },
        {
          key: "phone",
          label: "Phone",
          thClass: "border-top-0",
          tdClass: "align-middle",
        },
        {
          key: "company",
          label: "Company",
          thClass: "border-top-0",
          tdClass: "align-middle",
        },
        {
          key: "state",
          label: "State",
          thClass: "border-top-0",
          tdClass: "align-middle text-center",
        },
        {
          key: "stage",
          label: "Current Stage",
          thClass: "border-top-0",
          tdClass: "align-middle",
        },
        {
          key: "api_key_status",
          label: "API Key",
          thClass: "border-top-0",
          tdClass: "align-middle",
        },
        {
          key: "account_status",
          label: "Account",
          thClass: "border-top-0",
          tdClass: "align-middle",
        },
        {
          key: "application_started_at",
          label: "Application Start",
          thClass: "border-top-0",
          tdClass: "align-middle",
        },
        {
          key: "last_activity_at",
          label: "Last Activity",
          thClass: "border-top-0",
          tdClass: "align-middle",
        },
        {
          key: "incomplete_for",
          label: "Incomplete For",
          thClass: "border-top-0",
          tdClass: "align-middle",
        },
      ];
    },

    reportRows() {
      if (
          !Array.isArray(
              this.report_data
          )
      ) {
        return [];
      }

      return this.report_data.map(
          (item) => {
            return {
              id:
                  item.id ?? null,

              applicant_name:
                  item.applicant_name ??
                  item.user_name ??
                  "",

              email:
                  item.email ??
                  item.user_email ??
                  "",

              phone:
                  item.phone ??
                  item.company_phone ??
                  "",

              company_name:
                  item.company_name ??
                  "",

              license_number:
                  item.license_number ??
                  item.company_license_number ??
                  "",

              state:
                  item.state ??
                  item.company_state ??
                  "",

              stage:
                  item.stage ??
                  "",

              api_key_status:
                  item.api_key_status ??
                  "",

              account_status:
                  item.account_status ??
                  "",

              application_started_at:
                  item.application_started_at ??
                  item.step_1_completed_at ??
                  item.created_at ??
                  null,

              last_activity_at:
                  item.last_activity_at ??
                  item.updated_at ??
                  null,

              application_status:
                  item.application_status ??
                  "",

              current_step:
                  Number(
                      item.current_step ?? 0
                  ),
            };
          }
      );
    },
  },

  watch: {
    "filters.start_date": function (
        startDate
    ) {
      if (
          startDate ===
          moment()
              .startOf("month")
              .format("YYYY-MM-DD")
      ) {
        startDate = null;
      }

      this.setQueryVar(
          "start_date",
          startDate
      );
    },

    "filters.end_date": function (
        endDate
    ) {
      if (
          endDate ===
          moment()
              .format("YYYY-MM-DD")
      ) {
        endDate = null;
      }

      this.setQueryVar(
          "end_date",
          endDate
      );
    },

    "filters.search": function (
        search
    ) {
      this.setQueryVar(
          "search",
          search
      );
    },

    "filters.stage": function (
        stage
    ) {
      this.setQueryVar(
          "stage",
          stage
      );
    },

    "filters.sort": function (
        sort
    ) {
      this.setQueryVar(
          "sort",
          sort
      );
    },

    "filters.sort_direction": function (
        sortDirection
    ) {
      this.setQueryVar(
          "sort_direction",
          sortDirection
      );
    },
  },

  created() {
    this.loadReportData();
  },
};
</script>

<style scoped>
.sortable-column {
  cursor: pointer;
  user-select: none;
}

.incomplete-applications-search {
  width: 340px;
}
</style>